import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Paper,
  Collapse,
  Grid,
  Typography,
  Slider,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  VideoFile,
  Image,
  AudioFile,
  InsertDriveFile,
} from '@mui/icons-material';

import { MediaType, MediaStatus } from "../../types";
import type { SearchFilters, SortOptions } from "../../types";

interface MediaFiltersProps {
  filters: SearchFilters;
  sortOptions: SortOptions;
  onFiltersChange: (filters: SearchFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  onClearFilters: () => void;
  totalItems?: number;
}

const MediaFilters: React.FC<MediaFiltersProps> = ({
  filters,
  sortOptions,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalItems = 0,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [fileSizeRange, setFileSizeRange] = useState<number[]>([0, 1000]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleTypeChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      mediaTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      statuses: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleSortFieldChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      field: event.target.value,
    });
  };

  const handleSortDirectionChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      direction: event.target.value,
    });
  };

  const handleFileSizeChange = (event: Event, newValue: number | number[]) => {
    setFileSizeRange(newValue as number[]);
  };

  const handleFileSizeCommitted = (event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const [min, max] = newValue as number[];
    onFiltersChange({
      ...filters,
      fileSizeRange: [min * 1024 * 1024, max * 1024 * 1024], // Convert MB to bytes
    });
  };

  const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tagsArray = event.target.value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    onFiltersChange({
      ...filters,
      tags: tagsArray,
    });
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.mediaTypes && filters.mediaTypes.length > 0) count++;
    if (filters.statuses && filters.statuses.length > 0) count++;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.fileSizeRange && (filters.fileSizeRange[0] > 0 || filters.fileSizeRange[1] < 1000000000)) count++;
    return count;
  };

  const getTypeIcon = (type: MediaType) => {
    switch (type) {
      case MediaType.VIDEO:
        return <VideoFile fontSize="small" />;
      case MediaType.IMAGE:
        return <Image fontSize="small" />;
      case MediaType.AUDIO:
        return <AudioFile fontSize="small" />;
      default:
        return <InsertDriveFile fontSize="small" />;
    }
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Основная строка фильтров */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
        {/* Поиск */}
        <TextField
          placeholder={t('common.search')}
          value={filters.search || ''}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Тип медиафайла */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('media.type')}</InputLabel>
          <Select
            multiple
            value={filters.mediaTypes || []}
            onChange={handleTypeChange}
            label={t('media.type')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`media.types.${value.toLowerCase()}`)}
                    size="small"
                    icon={getTypeIcon(value as MediaType)}
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(MediaType).map((type) => (
              <MenuItem key={type} value={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTypeIcon(type)}
                  {t(`media.types.${type.toLowerCase()}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Статус */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            multiple
            value={filters.statuses || []}
            onChange={handleStatusChange}
            label={t('common.status')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`media.statuses.${value.toLowerCase()}`)}
                    size="small"
                    color={value === MediaStatus.READY ? 'success' : value === MediaStatus.ERROR ? 'error' : 'warning'}
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(MediaStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {t(`media.statuses.${status.toLowerCase()}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Сортировка */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('common.sortBy')}</InputLabel>
          <Select
            value={sortOptions.field || 'created_at'}
            onChange={handleSortFieldChange}
            label={t('common.sortBy')}
          >
            <MenuItem value="created_at">{t('common.dateCreated')}</MenuItem>
            <MenuItem value="updated_at">{t('common.dateUpdated')}</MenuItem>
            <MenuItem value="title">{t('common.title')}</MenuItem>
            <MenuItem value="file_size">{t('media.fileSize')}</MenuItem>
            <MenuItem value="duration">{t('media.duration')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t('common.order')}</InputLabel>
          <Select
            value={sortOptions.direction || 'desc'}
            onChange={handleSortDirectionChange}
            label={t('common.order')}
          >
            <MenuItem value="asc">{t('common.ascending')}</MenuItem>
            <MenuItem value="desc">{t('common.descending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
          >
            {t('common.filters')}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearFilters}
              color="error"
            >
              {t('common.clear')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Размер файла */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('media.fileSize')} (MB)
            </Typography>
            <Slider
              value={fileSizeRange}
              onChange={handleFileSizeChange}
              onChangeCommitted={handleFileSizeCommitted}
              valueLabelDisplay="auto"
              min={0}
              max={1000}
              step={10}
              marks={[
                { value: 0, label: '0' },
                { value: 100, label: '100' },
                { value: 500, label: '500' },
                { value: 1000, label: '1GB' },
              ]}
            />
          </Grid>

          {/* Теги */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('media.tags')}
              placeholder={t('media.tagsPlaceholder')}
              size="small"
              value={filters.tags?.join(', ') || ''}
              onChange={handleTagsChange}
              helperText={t('media.tagsHelper')}
            />
          </Grid>
        </Grid>

        {/* Информация о результатах */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.showingResults', { total: totalItems })}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MediaFilters; 
 
 
 